import { TICK_HZ } from '../sim/constants';
import type { Sim } from '../sim/sim';
import type { SceneId } from '../sim/types';
import type { AudioEngine } from './synth';

// Each scene has its own scale (MIDI notes), bass root and 8-step melody.
const MUSIC: Record<SceneId, { scale: number[]; root: number; melody: number[] }> = {
  pond: { scale: [60, 62, 64, 67, 69, 72, 74, 76], root: 48, melody: [0, 2, 4, 2, 5, 4, 2, 1] },
  swamp: { scale: [57, 60, 62, 64, 67, 69, 72], root: 45, melody: [0, 1, 3, 1, 2, 4, 3, 1] },
  storm: { scale: [62, 64, 65, 67, 69, 71, 72, 74], root: 50, melody: [0, 2, 3, 4, 3, 5, 4, 2] },
  ice: { scale: [76, 79, 81, 83, 86, 88], root: 52, melody: [0, 2, 1, 3, 2, 4, 3, 5] },
  heron: { scale: [65, 67, 69, 71, 72, 74, 76, 77], root: 53, melody: [0, 3, 2, 4, 6, 4, 3, 1] },
};

const LOOKAHEAD = 0.12;

/**
 * Schedules music on the rope's beat: a "plop" each time the rope passes
 * under the frog, a melody note when it is overhead, and soft ticks between.
 * The beat helps players time their jumps by ear.
 */
export class Music {
  private last = new Map<string, number>();
  private step = 0;
  private nextCricket = 0;

  constructor(private audio: AudioEngine) {}

  reset(): void {
    this.last.clear();
    this.step = 0;
  }

  update(sim: Sim, scene: SceneId, leadSec: number): void {
    const a = this.audio;
    if (!a.ready || !a.musicOn || sim.over) return;
    const p = sim.phase;
    const frac = p - Math.floor(p);
    const turnsPerSec = sim.speedAt(frac) * TICK_HZ;
    if (turnsPerSec <= 0) return;
    const latency = a.outputLatency + leadSec;
    const m = MUSIC[scene];

    const schedule = (key: string, offset: number, play: (at: number) => void) => {
      const target = Math.floor(p - offset) + 1 + offset;
      const dt = (target - p) / turnsPerSec - latency;
      if (dt < LOOKAHEAD && this.last.get(key) !== target) {
        this.last.set(key, target);
        play(a.now + Math.max(0.005, dt));
      }
    };

    schedule('bottom', 0, (at) => {
      a.plop(at);
      a.bass(at, m.root, 0.35);
    });
    schedule('top', 0.5, (at) => {
      if (sim.turn.double) a.plop(at, 1.25);
      a.marimba(at, m.scale[m.melody[this.step % m.melody.length] % m.scale.length]);
      this.step++;
    });
    schedule('q1', 0.25, (at) => a.tick(at));
    schedule('q3', 0.75, (at) => a.tick(at));

    if (scene === 'swamp' && a.now > this.nextCricket) {
      a.cricket(a.now + 0.05);
      this.nextCricket = a.now + 0.8 + Math.random() * 1.8;
    }
  }
}
